import { useMsal } from '@azure/msal-react';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useGrowthModelApiService } from '../../../api/providers/AppServiceProvider';
import { IGrowthModelApiService } from '../../../api/services/growth-model-api-service';
import {
	GrowthModelChangeLogQuery,
	GrowthModelDraftEdits,
	GrowthModelExportPayload,
	GrowthModelPublishPayload,
	GrowthModelUser,
	GrowthModelVersionKind,
	GrowthModelWorkbook,
} from '../../../models/growth-model/growth-model';
import { sanitizeCohortGuards } from './engine/cohort-guards';
import { GrowthModelEngine } from './engine/growth-model-engine';
import { loadGrowthModelForBoot } from './growth-model-boot';
import { sanitizeDriversForApi } from './sanitize-drivers-for-api';
import { sanitizeDraftEditsForApi } from './sanitize-draft-edits-for-api';

const GrowthModelContext = createContext<{ vm: any; bindSource?: string | null } | null>(null);

function channelBlocksOf(conf: any): string[] {
	if (Array.isArray(conf?.blocks) && conf.blocks.length) {
		return conf.blocks.map((b: string) => String(b).toLowerCase());
	}
	const present = ['subs', 'otp'].filter((b) => conf?.[b] && typeof conf[b] === 'object');
	return present.length ? present : ['otp'];
}

function normalizeWorkbook(data: GrowthModelWorkbook): GrowthModelWorkbook {
	const workbook = JSON.parse(JSON.stringify(data)) as GrowthModelWorkbook;
	if (!workbook.channels) workbook.channels = {};
	delete (workbook.channels as any).all;
	if (!Array.isArray(workbook.months)) workbook.months = [];
	const nulls = () => workbook.months.map(() => null);
	Object.keys(workbook.channels).forEach((ch) => {
		const conf: any = workbook.channels[ch];
		if (!conf) {
			delete workbook.channels[ch];
			return;
		}
		if (!Array.isArray(conf.acqRows)) conf.acqRows = [];
		conf.acqRows.forEach((row: any) => {
			if (!Array.isArray(row.values)) row.values = nulls();
		});
		const blocks = channelBlocksOf(conf);
		conf.blocks = blocks;
		blocks.forEach((b) => {
			if (!conf[b] || typeof conf[b] !== 'object') {
				conf[b] = { aov: nulls(), sales: nulls() };
			}
			if (!Array.isArray(conf[b].aov)) conf[b].aov = nulls();
			if (!Array.isArray(conf[b].sales)) conf[b].sales = nulls();
			if (conf.model === 'base' && !Array.isArray(conf[b].totalOrders)) {
				conf[b].totalOrders = nulls();
			}
			if (conf.model === 'cohort' && !Array.isArray(conf[b].cohorts)) {
				conf[b].cohorts = [];
			}
			if (Array.isArray(conf[b].cohorts)) {
				conf[b].cohorts.forEach((co: any) => {
					if (!Array.isArray(co.ratio)) co.ratio = nulls();
				});
			}
		});
		if (!conf.total || typeof conf.total !== 'object') {
			conf.total = { orders: nulls(), aov: nulls(), sales: nulls() };
		} else {
			if (!Array.isArray(conf.total.orders)) {
				conf.total.orders = Array.isArray((conf.total as any).totalOrders)
					? (conf.total as any).totalOrders
					: nulls();
			}
			if (!Array.isArray(conf.total.aov)) conf.total.aov = nulls();
			if (!Array.isArray(conf.total.sales)) conf.total.sales = nulls();
		}
	});
	if (!workbook.audit || typeof workbook.audit !== 'object') {
		(workbook as any).audit = {
			amzRefs: [],
			amzCount: 0,
			hardcodedFit: [],
			hardcodedCount: 0,
			iferrorBlankCount: 0,
		};
	}
	if (!workbook.settings || typeof workbook.settings !== 'object') {
		workbook.settings = {};
	}
	return workbook;
}

function errorMessage(err: any): string {
	const apiMessages = err?.response?.data?.Messages || err?.Messages;
	if (Array.isArray(apiMessages) && apiMessages.length) {
		return apiMessages.join('\n');
	}
	return err?.message || err?.toString?.() || 'Growth Model API unavailable';
}

export const useGrowthModel = () => {
	const ctx = useContext(GrowthModelContext);
	if (!ctx) {
		throw new Error('useGrowthModel must be used within GrowthModelProvider');
	}
	return ctx;
};

export const GrowthModelProviderWithApi: React.FC<{ api: IGrowthModelApiService; children: React.ReactNode }> = ({
	api,
	children,
}) => {
	const engineRef = useRef(new GrowthModelEngine());
	const idsRef = useRef<{ modelRevisionId?: string; planVersionId?: string; kind?: GrowthModelVersionKind; loadedId?: string }>({});
	const usersRef = useRef<GrowthModelUser[]>([]);
	const { accounts } = useMsal();
	const actorName = accounts[0]?.name || accounts[0]?.username || '';
	const [, setTick] = useState(0);
	const [ready, setReady] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [bindSource, setBindSource] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		const engine = engineRef.current;
		const cloneId = () => (idsRef.current.kind === 'clone' ? idsRef.current.planVersionId : undefined);
		const versionScope = () => (idsRef.current.kind && idsRef.current.kind !== 'main' ? idsRef.current.loadedId : undefined);
		const withVersion = (query: GrowthModelChangeLogQuery): GrowthModelChangeLogQuery => {
			const planVersionId = versionScope();
			return planVersionId ? { ...query, planVersionId } : query;
		};
		engine.onSaveDraft = async (edits: GrowthModelDraftEdits) => {
			if (idsRef.current.kind === 'published') return;
			try {
				const inputs = edits.inputs
					? Object.fromEntries(Object.entries(edits.inputs).filter(([ch]) => ch !== 'all'))
					: edits.inputs;
				const drivers = sanitizeDriversForApi(edits.drivers as any);
				await api.saveDraft(sanitizeDraftEditsForApi({ ...edits, inputs, drivers }), cloneId());
				setSaveError(null);
			} catch (err: any) {
				setSaveError(errorMessage(err));
				throw err;
			}
		};
		engine.onClientSaveError = (err: Error) => {
			setSaveError(errorMessage(err));
		};
		engine.onPublish = async (payload: GrowthModelPublishPayload) => {
			try {
				const st: any = engine.state;
				const view = engine.snapshotViewPeriods();
				const viewPeriods = {
					...(view.viewFrom !== null ? { viewFrom: view.viewFrom } : {}),
					...(view.viewTo !== null ? { viewTo: view.viewTo } : {}),
				};
				engine.setState({ publishing: true });
				const deletedDrivers = engine.deletedDriverKeys();
				const cohortGuards = engine.cohortGuardsForPublish();
				const result = await api.publishVersion({
					...payload,
					planVersionId: idsRef.current.planVersionId,
					edits: {
						inputs: st.inputs ?? undefined,
						rov: st.rov,
						mov: st.mov,
						cutoffIdx: st.cutoffIdx,
						fyStart: st.fyStart,
						ccy: st.ccy,
						amzCutoff: st.amzCutoff,
						rounding: st.rounding,
						...viewPeriods,
						rowCfg: st.rowCfg,
						rowOrd: st.rowOrd,
						rowDesc: st.rowDesc,
						scenarioOn: st.scenarioOn,
						drivers: sanitizeDriversForApi(engine.driversForApi()),
						deletedDrivers,
						scenarios: engine.scenariosForApi(),
						productMix: engine.snapshotProductMix(),
						sessionSources: engine.snapshotSessionSources(),
						cohortInput: st.cohortInput,
						...(cohortGuards ? { cohortGuards } : {}),
						cohortAuto: st.cohortAuto,
						flagReviews: st.flagReviews,
						overrideInfo: st.overrideInfo,
						mixInput: st.mixInput,
						cogsInput: st.cogsInput,
						srcInput: st.srcInput,
						cpsInput: st.cpsInput,
						overrides: st.serverOverrides,
					},
					cutoffIdx: st.cutoffIdx,
				});
				engine.confirmDeletedDrivers(deletedDrivers);
				if (idsRef.current.kind === 'clone') {
					engine.setState({ publishing: false, publishOpen: false, pubName: '', pubConfirm: '', dirtyAt: null });
					setSaveError(result.snowflakeWarning || null);
					await loadVersion(result.id);
					return;
				}
				engine.setState((s: any) => {
					const rec = {
						id: result.id,
						name: result.name,
						at: result.at,
						cutoffIdx:
							typeof result.workingCutoffIdx === 'number'
								? result.workingCutoffIdx
								: s.cutoffIdx === null || s.cutoffIdx === undefined
									? s.data?.cutoffIdx ?? null
									: s.cutoffIdx,
						inputs: JSON.parse(JSON.stringify(s.inputs)),
						rov: Object.assign({}, s.rov),
						mov: Object.assign({}, s.mov),
						settings: {
							fyStart: s.fyStart,
							ccy: s.ccy,
							amzCutoff: s.amzCutoff,
							rounding: s.rounding,
							cohortTenure: s.cohortTenure,
							viewFrom: view.viewFrom,
							viewTo: view.viewTo,
							rowCfg: s.rowCfg,
							rowOrd: s.rowOrd,
							rowDesc: s.rowDesc,
							scenarioOn: s.scenarioOn,
							drivers: engine.driversForApi(),
							productMix: engine.snapshotProductMix(),
							sessionSources: engine.snapshotSessionSources(),
							cohortInput: s.cohortInput,
							...(cohortGuards ? { cohortGuards } : {}),
							cohortAuto: s.cohortAuto,
							flagReviews: s.flagReviews,
							overrideInfo: s.overrideInfo,
							mixInput: s.mixInput,
							cogsInput: s.cogsInput,
							srcInput: s.srcInput,
							cpsInput: s.cpsInput,
						},
						cohortInput: s.cohortInput,
						cohortGuards: cohortGuards ? sanitizeCohortGuards(cohortGuards) : null,
						cohortAuto: s.cohortAuto,
						flagReviews: s.flagReviews,
						overrideInfo: s.overrideInfo,
						mixInput: s.mixInput,
						cogsInput: s.cogsInput,
					};
					const nameKey = String(rec.name || '').toLowerCase();
					const pubs = [rec].concat(
						(s.pubs || []).filter(
							(p: any) =>
								p.id !== rec.id && String(p.name || '').toLowerCase() !== nameKey
						)
					);
					const locks = Object.assign({}, s.locks);
					locks[rec.id] = true;
					const patch: any = {
						pubs,
						locks,
						savedAt: Date.now(),
						dirtyAt: null,
						savedDrivers: engine.driverSignatures(s.fdraft),
						publishing: false,
						publishOpen: false,
						pubName: '',
						pubConfirm: '',
					};
					if (result.planVersionId) {
						idsRef.current.planVersionId = result.planVersionId;
					}
					if (typeof result.workingCutoffIdx === 'number') {
						const actualMax =
							s.data && typeof s.data.actualCutoffIdx === 'number'
								? s.data.actualCutoffIdx
								: s.data && typeof s.data.cutoffIdx === 'number'
									? s.data.cutoffIdx
									: result.workingCutoffIdx;
						patch.cutoffIdx =
							result.workingCutoffIdx < actualMax ? result.workingCutoffIdx : null;
					}
					return patch;
				});
				setSaveError(result.snowflakeWarning || null);
			} catch (err: any) {
				engine.setState({ publishing: false });
				setSaveError(errorMessage(err));
			}
		};
		engine.onExportDone = (payload: GrowthModelExportPayload) => {
			void api.exportWorkbook(payload);
		};
		engine.changeLogLoader = (query: GrowthModelChangeLogQuery) => api.getChangeLog(withVersion(query));
		engine.changeLogBatchLoader = (batchId: string, query: GrowthModelChangeLogQuery) => api.getChangeLogBatch(batchId, withVersion(query));
		const unsub = engine.subscribe(() => setTick((n) => n + 1));
		let cancelled = false;

		const bootFrom = (raw: GrowthModelWorkbook, source?: string | null) => {
			const kind = raw.versionContext?.kind || 'main';
			idsRef.current = {
				modelRevisionId: raw.modelRevisionId,
				planVersionId: raw.planVersionId,
				kind,
				loadedId: raw.versionContext?.loadedId || raw.planVersionId,
			};
			setBindSource(source || raw.bindSource || 'none');
			const workbook = normalizeWorkbook(raw);
			if (kind === 'published') {
				workbook.canCreateVersion = workbook.canAuthor !== false;
				workbook.canAuthor = false;
			}
			engine.boot(workbook);
			if (raw.rov) engine.setState({ rov: raw.rov });
			if (raw.mov) engine.setState({ mov: raw.mov });
			engine.prepareCohortDraft();
			engine.setUsers(usersRef.current);
			engine.renderVals();
			if (process.env.NODE_ENV !== 'production') {
				(window as any).__GROWTH_ENGINE__ = engine;
			}
			setError(null);
			setReady(true);
		};

		const loadVersion = async (planVersionId?: string) => {
			setSaveError(null);
			setReady(false);
			try {
				const workbook = await api.getModel(planVersionId);
				if (cancelled) return;
				bootFrom(workbook, workbook.bindSource);
			} catch (err: any) {
				if (cancelled) return;
				setSaveError(errorMessage(err));
				setReady(true);
			}
		};

		engine.onSwitchVersion = (planVersionId: string | null) => loadVersion(planVersionId || undefined);

		engine.onCreateVersion = async (payload: { sourceVersionId: string; name: string; cutoffPeriod?: number | null }) => {
			try {
				setSaveError(null);
				const result = await api.cloneVersion(payload);
				if (cancelled) return;
				await loadVersion(result.planVersionId);
			} catch (err: any) {
				if (cancelled) return;
				setSaveError(errorMessage(err));
				throw err;
			}
		};

		engine.onRenameVersion = async (planVersionId: string, name: string) => {
			try {
				setSaveError(null);
				const result = await api.renameVersion(planVersionId, name);
				if (cancelled) return false;
				setSaveError(result.snowflakeWarning);
				return true;
			} catch (err: any) {
				if (!cancelled) setSaveError(errorMessage(err));
				return false;
			}
		};

		engine.onDeleteVersion = async (planVersionId: string) => {
			try {
				setSaveError(null);
				const result = await api.deleteVersion(planVersionId);
				if (cancelled) return false;
				if (idsRef.current.loadedId === planVersionId) await loadVersion();
				else engine.removeVersion(planVersionId);
				setSaveError(result.snowflakeWarning);
				return true;
			} catch (err: any) {
				if (!cancelled) setSaveError(errorMessage(err));
				return false;
			}
		};

		engine.refreshActualsHandler = async () => {
			if (idsRef.current.kind === 'published') {
				await loadVersion(idsRef.current.loadedId);
				return;
			}
			try {
				setSaveError(null);
				const result = await api.bindActuals(cloneId());
				if (cancelled) return;
				bootFrom(result.workbook, result.bindSource);
			} catch (err: any) {
				if (cancelled) return;
				setSaveError(errorMessage(err));
			}
		};

		api.getUsers()
			.then((users) => {
				if (cancelled) return;
				usersRef.current = users || [];
				engine.setUsers(usersRef.current);
			})
			.catch(() => undefined);

		(async () => {
			setReady(false);
			setError(null);
			try {
				const { workbook, bindSource: source } = await loadGrowthModelForBoot(api);
				if (cancelled) return;
				bootFrom(workbook, source);
			} catch (err: any) {
				if (cancelled) return;
				setReady(false);
				setError(errorMessage(err));
			}
		})();

		return () => {
			cancelled = true;
			unsub();
			engine.onSaveDraft = null;
			engine.onPublish = null;
			engine.onExportDone = null;
			engine.changeLogLoader = null;
			engine.changeLogBatchLoader = null;
			engine.refreshActualsHandler = null;
			engine.onClientSaveError = null;
			engine.onSwitchVersion = null;
			engine.onCreateVersion = null;
			engine.onRenameVersion = null;
			engine.onDeleteVersion = null;
		};
	}, [api, reloadKey]);

	useEffect(() => {
		if (ready) engineRef.current.setActor(actorName);
	}, [actorName, ready]);

	useEffect(() => {
		const engine = engineRef.current;
		const onBeforeUnload = (e: BeforeUnloadEvent) => {
			if (!engine.hasUnsavedChanges()) return;
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', onBeforeUnload);
			engine.destroy();
		};
	}, []);

	let vm: any = null;
	if (ready && !error) {
		try {
			vm = engineRef.current.renderVals();
		} catch {
			vm = null;
		}
	}

	if (error) {
		return (
			<div className="h-full min-h-0 flex items-center justify-center bg-[#F5F8FA] p-6" data-testid="growth-model-error">
				<div className="max-w-xl text-center text-[#08234C]">
					<div className="text-lg font-semibold mb-2">Growth Model failed to load from Snowflake</div>
					<div className="text-sm text-[#4D6389] mb-4 whitespace-pre-wrap">{error}</div>
					<button
						type="button"
						className="px-4 py-2 rounded bg-[#0052CD] text-white text-sm"
						onClick={() => setReloadKey((n) => n + 1)}
					>
						Retry load
					</button>
				</div>
			</div>
		);
	}

	if (!ready || !vm) {
		return (
			<div className="h-full min-h-0 flex flex-col items-center justify-center bg-[#F5F8FA] gap-3" data-testid="growth-model-loading">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052CD]" />
				<div className="text-sm text-[#4D6389]">Loading Growth Model…</div>
			</div>
		);
	}

	return (
		<GrowthModelContext.Provider value={{ vm, bindSource }}>
			{saveError && (
				<div className="bg-[#FDECEC] text-[#8A1F1F] text-sm px-4 py-2 border-b border-[#F5C2C2]" data-testid="growth-model-save-error">
					<div className="font-medium mb-1">Growth Model error</div>
					<div className="whitespace-pre-wrap">{saveError}</div>
				</div>
			)}
			{children}
		</GrowthModelContext.Provider>
	);
};

export const GrowthModelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const api = useGrowthModelApiService();
	return <GrowthModelProviderWithApi api={api}>{children}</GrowthModelProviderWithApi>;
};
